// The GARAGE (P1, pivot spec §3/§5/§11): pre-incorporation home screen.
// Founder strip and the ramen clock, the one-pool allocation rows, the pitch
// (score breakdown per the transparency principle — every factor is a number
// the player can move), the seed sheet whose acceptance founds the company,
// and the fold. Everything here queues commands; END TURN resolves the month.

import { useState } from "react";
import type { ContentPack, GarageActivity, PlayerView, TechNodeDef } from "../../engine/types.js";
import { DISCIPLINES, GARAGE_ACTIVITIES } from "../../engine/types.js";
import { interpolate } from "../../engine/calendar.js";
import { totalShares } from "../../engine/systems/finance.js";
import { pitchAppealParts, pitchScore } from "../../engine/systems/garage.js";
import { effectiveEmCost } from "../../engine/systems/tree.js";
import { activeTreeOf, viewCtx, type ScreenProps } from "./shared.js";
import { money, monthLabel, pct } from "../format.js";
import { Bar } from "../components/widgets.js";
import { ThesisNotes } from "../components/ThesisNotes.js";

const ACTIVITY_LABEL: Record<GarageActivity, string> = {
  research: "RESEARCH THE THESIS",
  demo: "BUILD THE DEMO",
  discovery: "CUSTOMER DISCOVERY",
  consulting: "MOONLIGHT CONSULTING",
  networking: "NETWORK WITH VCS",
  recruiting: "RECRUIT THE TEAM",
};

export function Garage({ snap, content, session }: ScreenProps) {
  const { view, pending } = snap;
  if (!view.garage) return null; // incorporation day: the router is re-homing
  const g = view.garage;
  const cfg = content.economy.garage;
  const gdef = content.scenario.garage!;
  const m = view.monthIndex;
  const tree = activeTreeOf(content);
  const ctx = viewCtx(view, content);

  // queued allocation overrides the live one (replace-keyed: ≤1 pending)
  const queuedAlloc = pending.find((c) => c.type === "setGarageAlloc");
  const alloc: Partial<Record<GarageActivity, number>> =
    queuedAlloc && queuedAlloc.type === "setGarageAlloc" ? queuedAlloc.alloc : g.alloc;
  const focusId =
    queuedAlloc && queuedAlloc.type === "setGarageAlloc" && queuedAlloc.researchNodeId !== undefined
      ? queuedAlloc.researchNodeId
      : g.researchNodeId;

  const dayJob = g.savings <= 0;
  const burn = g.founders.length * gdef.personalBurnMonthly;
  const available = g.founders.length * (dayJob ? cfg.dayJobEff : 1);
  const requested = GARAGE_ACTIVITIES.reduce((a, k) => a + (alloc[k] ?? 0), 0);

  const setAlloc = (k: GarageActivity, v: number) => {
    const next = { ...alloc, [k]: Math.max(0, v) };
    session.queue({ type: "setGarageAlloc", alloc: next, researchNodeId: focusId ?? null });
  };
  const setFocus = (id: string | null) => {
    session.queue({ type: "setGarageAlloc", alloc: { ...alloc }, researchNodeId: id });
  };

  // garage-researchable nodes the engine would accept as a focus right now
  const researchable = (tree?.nodes ?? []).filter(
    (n) =>
      n.garageResearchable &&
      n.availableMonth <= m &&
      !(n.id in view.tree.researched) &&
      n.prereqs.every((p) => p in view.tree.researched),
  );
  const focusNode = focusId ? (tree?.nodes.find((n) => n.id === focusId) ?? null) : null;

  const hires = DISCIPLINES.reduce((a, d) => a + g.committedHires[d], 0);

  return (
    <div>
      <h2>THE GARAGE — {monthLabel(m)}</h2>

      <FoundersPanel snap={snap} content={content} burn={burn} dayJob={dayJob} />

      <ThesisBets view={view} content={content} focusId={focusId ?? null} setFocus={setFocus} />

      <div className="panel">
        <div className="title">
          THE MONTH — {available.toFixed(available % 1 === 0 ? 0 : 2)} founder-months to spend
          {requested > available && <span className="warn-text"> · over-asked: everything scales to fit</span>}
        </div>
        {GARAGE_ACTIVITIES.map((k) => (
          <div key={k} style={{ display: "flex", gap: 10, alignItems: "center", padding: "3px 0", flexWrap: "wrap" }}>
            <span style={{ minWidth: 190 }}>{ACTIVITY_LABEL[k]}</span>
            <span className="stepper">
              <button onClick={() => setAlloc(k, (alloc[k] ?? 0) - 0.5)}>‹</button>
              <span className={`val${queuedAlloc ? " queued" : ""}`}>{(alloc[k] ?? 0).toFixed(1)}</span>
              <button onClick={() => setAlloc(k, (alloc[k] ?? 0) + 0.5)}>›</button>
            </span>
            <ActivityHint k={k} snap={snap} content={content} researchable={researchable} focusId={focusId ?? null} setFocus={setFocus} />
          </div>
        ))}
        <span className="hint">
          one pool, six doors — the founders {dayJob ? `are day-jobbing (${cfg.dayJobEff} fm each, evenings and weekends)` : "are full-time while the savings last"}.
          Archetype bonuses: the dealmaker networks at ×{cfg.dealmakerNetworkingMult}, the operator recruits at ×{cfg.operatorRecruitingMult},
          the technologist tilts research grades toward A.
        </span>
      </div>

      {focusNode && !(focusNode.id in view.tree.researched) && (
        <div className="panel">
          <div className="title">RESEARCH FOCUS — {focusNode.displayName.toUpperCase()}</div>
          <Bar
            frac={(view.tree.inProgress[focusNode.id] ?? 0) / effectiveEmCost(ctx, focusNode)}
            label={focusNode.exclusiveGroup === "thesis" ? "the founding thesis" : "garage research"}
            right={`${(view.tree.inProgress[focusNode.id] ?? 0).toFixed(1)} / ${effectiveEmCost(ctx, focusNode).toFixed(0)} EM`}
          />
          <span className="hint">founder-months become engineer-months 1:1 here — see the TREE screen for the whole map</span>
        </div>
      )}

      <PitchPanel snap={snap} content={content} session={session} />

      <div className="panel">
        <div className="title">WHAT YOU'VE BUILT</div>
        <div className="row">
          <div className="tile">
            <div className="k">DEMO</div>
            <div className="v">
              stage {g.demoStage}/{cfg.demoStages}
            </div>
            <div className="sub">multiplies the pitch ×{(1 + cfg.pitch.demoMult * (g.demoStage / cfg.demoStages)).toFixed(2)} now</div>
          </div>
          <div className="tile">
            <div className="k">WARM INTROS</div>
            <div className="v">
              level {g.warmIntro}/{cfg.warmIntroMax}
            </div>
            <div className="sub">{g.introSpent} spent · each pitch burns one; cold calls pitch at ×{cfg.pitch.coldCallFactor}</div>
          </div>
          <div className="tile">
            <div className="k">COMMITTED HIRES</div>
            <div className="v">{hires}</div>
            <div className="sub">
              {DISCIPLINES.map((d) => `${g.committedHires[d]}${d[0]!.toUpperCase()}`).join(" / ")} — they join on funding day
            </div>
          </div>
          <div className="tile">
            <div className="k">SEGMENTS STUDIED</div>
            <div className="v">{g.discovered.length}</div>
            <div className="sub">
              {g.discovered.length > 0
                ? g.discovered.map((id) => content.markets.find((mk) => mk.id === id)?.displayName.split("—").pop()?.trim() ?? id).join(", ")
                : "discovery grants a first-chip fit bonus per segment"}
            </div>
          </div>
        </div>
      </div>

      <FoldPanel snap={snap} session={session} />
    </div>
  );
}

/** THE FOUNDING BET (P1): the four theses side by side, trade-offs framed the
 *  way a 1993 founder would weigh them (ThesisNotes) plus derived pricing —
 *  effective EM cost (which is also the pivot price once a sibling is held),
 *  banked progress, and how the pitch hears each bet today. */
function ThesisBets({
  view,
  content,
  focusId,
  setFocus,
}: {
  view: PlayerView;
  content: ContentPack;
  focusId: string | null;
  setFocus: (id: string | null) => void;
}) {
  const tree = activeTreeOf(content);
  if (!tree) return null;
  const ctx = viewCtx(view, content);
  const m = view.monthIndex;
  const theses = tree.nodes.filter((n) => n.exclusiveGroup === "thesis" && n.availableMonth <= m);
  if (theses.length === 0) return null;
  const held = theses.find((n) => n.id in view.tree.researched);
  return (
    <div className="panel">
      <div className="title">
        {held ? "YOUR FOUNDING BET — and what changing it would cost" : "THE FOUNDING BET — the game's defining decision"}
      </div>
      <div className="thesis-grid">
        {theses.map((n) => {
          const done = view.tree.researched[n.id];
          const banked = view.tree.inProgress[n.id] ?? 0;
          const cost = effectiveEmCost(ctx, n);
          const appeal = n.pitchAppeal ? interpolate(n.pitchAppeal, m) : 0;
          const isFocus = focusId === n.id;
          return (
            <div className="card" key={n.id}>
              <div className="head">
                <span className="id" style={{ color: done ? "var(--amber)" : "var(--ink)" }}>{n.displayName}</span>
                {done && <span className={`badge ${done.grade === "A" ? "cyan" : done.grade === "B" ? "" : "amber"}`}>{done.grade}</span>}
              </div>
              <ThesisNotes node={n} view={view} content={content} showWindow />
              <div className="hint">
                research {cost.toFixed(0)} EM{banked > 0 ? ` · ${banked.toFixed(1)} banked` : ""}
                {appeal > 0 ? ` · the pitch hears this at ${appeal.toFixed(2)} today` : ""}
                {held && !done ? ` — the ×${content.economy.tree.exclusivePenaltyMult} unlearning tax is priced in` : ""}
              </div>
              {banked > 0 && !done && <Bar frac={banked / cost} label="research" right={`${banked.toFixed(1)} / ${cost.toFixed(0)} EM`} />}
              {done ? (
                <div className="warn-text" style={{ fontSize: 11, marginTop: 4 }}>
                  your founding bet — grade {done.grade}
                </div>
              ) : (
                <div className="actions">
                  <button className={`act${isFocus ? " queued" : ""}`} onClick={() => setFocus(isFocus ? null : n.id)}>
                    {isFocus ? "★ THE BET — click to clear" : held ? "PIVOT TO THIS" : "MAKE THIS THE BET"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <span className="hint">
        soft-exclusive: nothing is ever locked, but a second thesis costs ×{content.economy.tree.exclusivePenaltyMult} (organizational
        unlearning), less any salvage credits the first one banks.
      </span>
    </div>
  );
}

function FoundersPanel({ snap, content, burn, dayJob }: { snap: ScreenProps["snap"]; content: ScreenProps["content"]; burn: number; dayJob: boolean }) {
  const g = snap.view.garage!;
  const gdef = content.scenario.garage!;
  const start =
    gdef.potStart + gdef.founders.reduce((a, f) => a + f.savingsMonths * gdef.personalBurnMonthly, 0);
  const monthsLeft = burn > 0 ? g.savings / burn : 999;
  return (
    <div className="panel">
      <div className="title">THE FOUNDERS — and the ramen clock</div>
      <div className="row">
        {g.founders.map((f) => (
          <div className="tile" key={f.name}>
            <div className="k">{f.name.toUpperCase()}</div>
            <div className="v" style={{ fontSize: 14 }}>
              {f.archetype.toUpperCase()}
            </div>
            <div className="sub">{f.discipline} · 1.0 fm/mo{dayJob ? ` → ${content.economy.garage.dayJobEff} (day job)` : ""}</div>
          </div>
        ))}
        <div className="tile">
          <div className="k">POOLED SAVINGS</div>
          <div className={`v${g.savings < 2 * burn ? " bad" : g.savings < 6 * burn ? " warn" : ""}`}>{money(g.savings)}</div>
          <div className="sub">
            burn {money(burn)}/mo · {dayJob ? "gone — everyone's day-jobbing" : `~${Math.floor(monthsLeft)}mo of ramen left`}
          </div>
        </div>
      </div>
      <Bar frac={g.savings / start} amber={g.savings < 6 * burn} label="runway (savings + the pot)" right={`${money(g.savings)} of ${money(start)}`} />
      {dayJob && (
        <span className="warn-text">
          Savings are gone. Nobody leaves — but everyone's back to evenings and weekends, and the field is not waiting for you.
        </span>
      )}
    </div>
  );
}

/** Per-activity effect line: what the next founder-month actually buys. */
function ActivityHint({
  k,
  snap,
  content,
  researchable,
  focusId,
  setFocus,
}: {
  k: GarageActivity;
  snap: ScreenProps["snap"];
  content: ScreenProps["content"];
  researchable: TechNodeDef[];
  focusId: string | null;
  setFocus: (id: string | null) => void;
}) {
  const g = snap.view.garage!;
  const cfg = content.economy.garage;
  const m = snap.view.monthIndex;
  switch (k) {
    case "research":
      return (
        <>
          <select value={focusId ?? ""} onChange={(e) => setFocus(e.target.value || null)}>
            <option value="">— pick a node —</option>
            {researchable.map((n) => {
              const a = n.pitchAppeal ? interpolate(n.pitchAppeal, m) : 0;
              const pitchTag =
                n.exclusiveGroup === "thesis"
                  ? ` (founding thesis · pitch ${a.toFixed(2)})`
                  : a > 0
                    ? ` (pitch +${a.toFixed(2)} once the bet lands)`
                    : " (silicon payoff — the pitch doesn't hear it)";
              return (
                <option key={n.id} value={n.id}>
                  {n.displayName}
                  {pitchTag}
                </option>
              );
            })}
          </select>
          <span className="hint">the defining decision — theses are soft-exclusive (×{content.economy.tree.exclusivePenaltyMult} to switch later)</span>
        </>
      );
    case "demo":
      return (
        <span className="hint">
          stage {g.demoStage}/{cfg.demoStages} · {(g.demoEm % cfg.demoEmPerStage).toFixed(1)}/{cfg.demoEmPerStage} EM to next
          {Object.keys(snap.view.tree.researched).length + Object.keys(snap.view.tree.inProgress).length === 0
            ? " · needs research to show — a demo of nothing demos nothing"
            : ""}
        </span>
      );
    case "discovery":
      return (
        <span className="hint">
          {(g.discoveryEm % cfg.discoveryEmPerSegment).toFixed(1)}/{cfg.discoveryEmPerSegment} EM to the next segment · permanent
          first-chip fit bonus where you've listened
        </span>
      );
    case "consulting":
      return <span className="hint">+{money(interpolate(cfg.consultingRatePerFm, m))}/fm into savings — runway bought with the time it's buying</span>;
    case "networking":
      return (
        <span className="hint">
          level {g.warmIntro}/{cfg.warmIntroMax} · {(g.networkingEm % cfg.networkingEmPerLevel).toFixed(1)}/{cfg.networkingEmPerLevel} EM to next
        </span>
      );
    case "recruiting":
      return (
        <span className="hint">
          {DISCIPLINES.reduce((a, d) => a + g.committedHires[d], 0)}/{cfg.recruitMax} committed ·{" "}
          {(g.recruitEm % cfg.recruitEmPerHire).toFixed(1)}/{cfg.recruitEmPerHire} EM to next — instant headcount on incorporation day
        </span>
      );
  }
}

/** The pitch: live score breakdown, the schedule button, and the seed sheet. */
function PitchPanel({ snap, content, session }: ScreenProps) {
  const { view, pending } = snap;
  const g = view.garage!;
  const cfg = content.economy.garage;
  const m = view.monthIndex;
  const ctx = viewCtx(view, content);
  const tree = activeTreeOf(content);

  // the engine's own appeal split, so the breakdown always multiplies to the score
  const parts = pitchAppealParts(ctx);
  const appeal = parts.thesis + (parts.thesisHeld ? parts.tech : 0);
  const demoFactor = 1 + cfg.pitch.demoMult * (g.demoStage / cfg.demoStages);
  const hires = DISCIPLINES.reduce((a, d) => a + g.committedHires[d], 0);
  const teamFactor = 1 + cfg.pitch.teamPerHireMult * hires;
  const hasIntro = g.warmIntro > g.introSpent;
  const introFactor = hasIntro ? 1 + cfg.pitch.introMult * g.warmIntro : cfg.pitch.coldCallFactor;
  const climate = 0.6 + 0.8 * interpolate(content.economy.sentiment, m);
  const stinging = g.lastPitchMonth !== null && m - g.lastPitchMonth <= cfg.pitch.failStingMonths && g.pitchesFailed > 0;
  const score = pitchScore(ctx);

  const cooldown = g.lastPitchMonth !== null && m - g.lastPitchMonth < cfg.pitch.cooldownMonths;
  const sheet = view.finance.openTermSheet;
  const pitchQueued = pending.some((c) => c.type === "schedulePitch");

  const factor = (label: string, v: number, note?: string) => (
    <tr>
      <td>{label}</td>
      <td className="r">
        <b>×{v.toFixed(2)}</b>
      </td>
      <td className="dim">{note ?? ""}</td>
    </tr>
  );

  return (
    <div className="panel">
      <div className="title">THE PITCH — partners pass below {cfg.pitch.scoreFloor.toFixed(2)}</div>
      <div className="scroll-x" style={{ maxWidth: 560 }}>
        <table>
          <tbody>
            <tr>
              <td>thesis appeal</td>
              <td className="r">
                <b>{parts.thesis.toFixed(2)}</b>
              </td>
              <td className="dim">
                {parts.thesis > 0
                  ? "your founding bet × grade, era-keyed"
                  : parts.tech > 0
                    ? "tech without a thesis — partners fund stories, not features"
                    : "you have researched nothing — there is no pitch"}
              </td>
            </tr>
            {parts.tech > 0 && (
              <tr>
                <td>+ tech credibility</td>
                <td className="r">
                  <b className={parts.thesisHeld ? "" : "bad-text"}>+{(parts.thesisHeld ? parts.tech : 0).toFixed(2)}</b>
                </td>
                <td className="dim">
                  {parts.thesisHeld
                    ? "researched tech backing the story × grade, era-keyed"
                    : `+${parts.tech.toFixed(2)} locked until a founding bet lands`}
                </td>
              </tr>
            )}
            {factor("demo", demoFactor, `stage ${g.demoStage}/${cfg.demoStages}`)}
            {factor("team", teamFactor, `${hires} committed hires`)}
            {factor(
              hasIntro ? "warm intro" : "cold call",
              introFactor,
              hasIntro ? `level ${g.warmIntro} — one charge burns per pitch` : "network first: cold calls read desperate",
            )}
            {factor("funding climate", climate)}
            {stinging &&
              factor(
                "the Valley talks",
                Math.pow(cfg.pitch.failStingFactor, g.pitchesFailed),
                `${g.pitchesFailed} recent failed pitch${g.pitchesFailed === 1 ? "" : "es"} — fades after ${cfg.pitch.failStingMonths}mo`,
              )}
            <tr>
              <td>
                <b>pitch score</b>
              </td>
              <td className="r">
                <b className={score >= cfg.pitch.scoreFloor ? "" : "bad-text"}>{score.toFixed(2)}</b>
              </td>
              <td className="dim">seed size and pre-money ride this</td>
            </tr>
          </tbody>
        </table>
      </div>
      {!sheet && (
        <div className="actions" style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button
            className={`act warn${pitchQueued ? " queued" : ""}`}
            disabled={cooldown && !pitchQueued}
            onClick={() =>
              pitchQueued
                ? session.unqueue(pending.findIndex((c) => c.type === "schedulePitch"))
                : session.queue({ type: "schedulePitch" })
            }
          >
            {pitchQueued ? "PITCH QUEUED ✕" : "SCHEDULE THE PITCH ▸"}
          </button>
          <span className="hint">
            {cooldown
              ? "partners' calendars fill up — give it a month"
              : `resolves on END TURN · a failure stings ×${cfg.pitch.failStingFactor} for ${cfg.pitch.failStingMonths} months`}
          </span>
        </div>
      )}
      {sheet && <SeedSheet snap={snap} content={content} session={session} />}
    </div>
  );
}

/** The seed sheet on the table. Accepting it IS incorporation (spec §5.2). */
function SeedSheet({ snap, content, session }: ScreenProps) {
  const { view, pending } = snap;
  const fin = view.finance;
  const sheet = fin.openTermSheet!;
  const total = totalShares(fin);
  // closeRound's dilution math, mirrored for the preview (same as Boardroom)
  const investorNew = sheet.amount / (sheet.preMoney / total);
  const poolAdd = Math.max(0, (sheet.poolTopUpPct * (total + investorNew) - fin.poolShares) / (1 - sheet.poolTopUpPct));
  const shareAfter = fin.founderShares / (total + investorNew + poolAdd);
  const answer = pending.find((c) => c.type === "acceptTermSheet" || c.type === "rejectTermSheet");
  const g = view.garage!;
  const hires = DISCIPLINES.reduce((a, d) => a + g.committedHires[d], 0);
  const founderHeads = Object.values(content.scenario.founders).reduce((a, b) => a + b, 0);

  return (
    <div style={{ marginTop: 10, border: "1px solid var(--amber-dim)", padding: "8px 10px" }}>
      <div className="title" style={{ color: "var(--amber)" }}>
        A SEED SHEET IS ON THE TABLE — expires in {sheet.monthsLeft} month{sheet.monthsLeft === 1 ? "" : "s"}
      </div>
      <div className="row">
        <div className="tile">
          <div className="k">CHECK</div>
          <div className="v">{money(sheet.amount)}</div>
          <div className="sub">at {money(sheet.preMoney)} pre-money</div>
        </div>
        <div className="tile">
          <div className="k">YOU'D OWN</div>
          <div className="v">{pct(shareAfter, 0)}</div>
          <div className="sub">after the round and the {pct(sheet.poolTopUpPct, 0)} option pool</div>
        </div>
        <div className="tile">
          <div className="k">DAY-ONE COMPANY</div>
          <div className="v">{founderHeads + hires} people</div>
          <div className="sub">
            {founderHeads} founders + {hires} committed hires{sheet.boardSeat ? " · lead takes a board seat" : ""}
          </div>
        </div>
      </div>
      <div className="actions" style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
        <button
          className={`act warn${answer?.type === "acceptTermSheet" ? " queued" : ""}`}
          onClick={() =>
            answer?.type === "acceptTermSheet"
              ? session.unqueue(pending.findIndex((c) => c.type === "acceptTermSheet"))
              : session.queue({ type: "acceptTermSheet" })
          }
        >
          {answer?.type === "acceptTermSheet" ? "SIGNING QUEUED ✕" : "SIGN — FOUND THE COMPANY ▸"}
        </button>
        <button
          className={`act bad${answer?.type === "rejectTermSheet" ? " queued" : ""}`}
          onClick={() =>
            answer?.type === "rejectTermSheet"
              ? session.unqueue(pending.findIndex((c) => c.type === "rejectTermSheet"))
              : session.queue({ type: "rejectTermSheet" })
          }
        >
          {answer?.type === "rejectTermSheet" ? "PASS QUEUED ✕" : "pass — keep pitching"}
        </button>
        <span className="hint">signing incorporates on END TURN: cap table, payroll, the board — the clock becomes real. Dithering reads like weakness.</span>
      </div>
    </div>
  );
}

/** Folding is game over — two clicks, and the copy doesn't sugarcoat it. */
function FoldPanel({ snap, session }: { snap: ScreenProps["snap"]; session: ScreenProps["session"] }) {
  const [armed, setArmed] = useState(false);
  const { pending } = snap;
  const foldQueued = pending.some((c) => c.type === "foldCompany");
  return (
    <div className="panel">
      <div className="title">THE DRAWER</div>
      {!armed && !foldQueued && (
        <button className="act bad" onClick={() => setArmed(true)}>
          FOLD — go back to your old job
        </button>
      )}
      {(armed || foldQueued) && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button
            className={`act bad${foldQueued ? " queued" : ""}`}
            onClick={() =>
              foldQueued
                ? session.unqueue(pending.findIndex((c) => c.type === "foldCompany"))
                : session.queue({ type: "foldCompany" })
            }
          >
            {foldQueued ? "FOLD QUEUED ✕" : "YES — THE NAPKINS GO IN A DRAWER (game over, scored $0)"}
          </button>
          {!foldQueued && (
            <button className="act" onClick={() => setArmed(false)}>
              no — keep going
            </button>
          )}
        </div>
      )}
      <div className="hint" style={{ marginTop: 4 }}>
        Of the ~70 companies that chased 3D graphics in the mid-90s, most never incorporated at all.
      </div>
    </div>
  );
}
